import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getDisconnectImpact } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/** Disconnect impact groups for a provider (CT-4187). Fetches only while the alert is open. */
export function useDisconnectImpact(slug, isOpen) {
  const { t } = useTranslation('admin-studio');
  const [impact, setImpact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!isOpen) return;
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getDisconnectImpact(slug);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setImpact(validateApiResponse(res, t('common.requestFailed')).body ?? null);
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setImpact(null);
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [isOpen, slug, t]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(() => ({ impact, isLoading, error }), [impact, isLoading, error]);
}
